/**
 * Fills the brand name into the terms-of-sale legal text shown next to the PO / discount-code box
 * and on the review pages. Callers:
 * `Shared/PurchaseOrderDiscountCode/PurchaseOrderDiscountCode.tsx`,
 * `ExpressCheckout/ExpressCheckout.tsx`, `OrderReview/Context/OrderReviewContext.tsx`.
 *
 * The brand name is `NEXT_PUBLIC_COMPANY` (set per brand build) turned into words by
 * `Shared/ProcessingDescription/ProcessingDescription.tsx` → `camelToPretty`, for example
 * `southernToolSteel` → "Southern Tool Steel"; "Ryerson" when unset. It is read once, at module
 * load, from the build-time value.
 */
import { camelToPretty } from "@components/Shared/ProcessingDescription/ProcessingDescription";
import { Language } from "@ryerson/frontend.application";

import { PHONE_NUMBERS } from "@ryerson/common.constants/dist/phoneNumbers";
import { Country } from "@ryerson/frontend.form-controls";

const displayCompanyName = camelToPretty(process.env.NEXT_PUBLIC_COMPANY || "Ryerson");

interface HasLegalText {
	legalTextOne: string;
	legalTextTwo: string;
}

/**
 * Returns a copy of `contentObject` with every `%company%` in `legalTextOne` and `legalTextTwo`
 * replaced by the brand name. Other fields are untouched. `language` is not read.
 */
export const replaceContentWithCompanyName = <T extends HasLegalText>(
	language: Language,
	contentObject: T
) => {
	const legalTextOne = contentObject.legalTextOne.replace(/%company%/g, displayCompanyName);
	const legalTextTwo = contentObject.legalTextTwo.replace(/%company%/g, displayCompanyName);
	return {
		...contentObject,
		legalTextOne,
		legalTextTwo,
	} as T;
};

/** Not referenced in this repository; `getBrandInfo` from `@ryerson/frontend.common` is used. */
export type BrandInfo = {
	name: string;
	email: string;
	phone: string;
};
